import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { Customer, Sale, Store } from "@/api/types";
import { Badge, Button, Drawer, Field, Input, Select, useToast } from "@/components/ui";
import { PersonPicker } from "@/components/AppointmentDrawer";
import { ProductLines, type ProductLine, linesTotal } from "@/components/ProductLines";

type Line = { method: string; amount: string };
const money = (v: string | number) => (Math.round(Number(v) * 100) / 100).toFixed(2);

/** Stand-alone product sale (P10 §27.9): a walk-in or a known customer buys rings / necklaces without an appointment. */
export function SaleDialog({ store, onClose }: { store: Store; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [fresh, setFresh] = useState<{ first_name: string; last_name: string; phone: string; email: string } | null>(null);
  const [lines, setLines] = useState<ProductLine[]>([]);
  const [discount, setDiscount] = useState("0");
  const [pay, setPay] = useState<Line[]>([{ method: store.payment_methods?.[0] ?? "cash", amount: "0.00" }]);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const subtotal = linesTotal(lines);
  const total = Math.max(0, Math.round((subtotal - Number(discount || 0)) * 100) / 100);
  useEffect(() => { if (pay.length === 1) setPay([{ ...pay[0], amount: money(total) }]); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [total]);
  const sum = pay.reduce((acc, l) => acc + Number(l.amount || 0), 0);
  const diff = Math.round((total - sum) * 100) / 100;
  const m = useMutation({
    mutationFn: async () => {
      let customerId = customer?.id ?? null;
      if (!customerId && fresh && fresh.first_name && fresh.phone) {
        const c = await api<Customer>("/app/customers", { method: "POST", body: JSON.stringify({ brand_id: store.brand_id, ...fresh, email: fresh.email || null }) });
        customerId = c.id;
      }
      return api<Sale>("/app/sales", { method: "POST", body: JSON.stringify({ store_id: store.id, customer_id: customerId, lines: lines.map((l) => ({ product_id: l.product.id, qty: l.qty, unit_price: l.unit_price })), discount_amount: discount || null, payments: pay.filter((l) => Number(l.amount) > 0), note: note || null }) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["sales"] }); toast.show(t("retail.saleDone")); onClose(); },
    onError: (e) => { let reason = e instanceof ApiError ? e.detail : ""; try { reason = JSON.parse(reason).reason; } catch { /* plain */ } setError(t(`checkout.err.${reason}`, { defaultValue: reason || t("app.error") })); },
  });
  const methods = store.payment_methods ?? ["cash", "pos", "iris"];
  const setLine = (i: number, patch: Partial<Line>) => setPay(pay.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <Drawer open onClose={onClose} title={t("retail.sell")} footer={<><Button type="button" variant="secondary" onClick={onClose}>{t("app.cancel")}</Button><Button form="sale-form" disabled={m.isPending || lines.length === 0 || Math.abs(diff) > 0.004} title={Math.abs(diff) > 0.004 ? t("checkout.mustBalance") : undefined}>{t("retail.completeSale")}</Button></>}>
      <form id="sale-form" className="space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); setError(null); m.mutate(); }}>
        <PersonPicker brandId={store.brand_id} label={`${t("appt.customer")} (${t("retail.optional")})`} value={customer} onChange={setCustomer} fresh={fresh} onFresh={setFresh} />
        <ProductLines brandId={store.brand_id} lines={lines} onChange={setLines} />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Field label={t("checkout.discount")}><Input type="number" step="0.01" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} /></Field>
          <div className="text-xs text-muted sm:col-span-2">{t("checkout.payable")}<br /><b className="text-lg text-ink">{money(total)} €</b></div>
        </div>
        {pay.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <Select className="!w-32" value={l.method} onChange={(e) => setLine(i, { method: e.target.value })}>{methods.map((pm) => <option key={pm} value={pm}>{t(`checkout.method.${pm}`)}</option>)}</Select>
            <Input className="!w-28" type="number" step="0.01" min="0" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} />
            {pay.length > 1 && <button type="button" className="text-xs" onClick={() => setPay(pay.filter((_, j) => j !== i))}>✕</button>}
          </div>
        ))}
        <div className="flex items-center gap-3 text-xs">
          <button type="button" className="underline" onClick={() => setPay([...pay, { method: methods[1] ?? methods[0], amount: money(Math.max(0, diff)) }])}>{t("checkout.split")}</button>
          {Math.abs(diff) > 0.004 && <Badge tone="amber">{diff > 0 ? t("checkout.missing", { amount: money(diff) }) : t("checkout.over", { amount: money(-diff) })}</Badge>}
        </div>
        <Field label={t("appt.notes")}><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        {error && <p className="text-danger" role="alert">{error}</p>}
      </form>
    </Drawer>
  );
}
